/**
 * AI domain API module (AI-SPM + AI Assistant + AI-SPM lite).
 *
 * Real per-domain split — this file used to be a stub re-export from ../api.
 * The three exports below (`aiSecurityApi`, `aiAssistantApi`, `aiSpmApi`) were
 * extracted verbatim from `frontend/src/services/api.ts` so consumers can
 * import them directly from `services/api/ai` without round-tripping through
 * the monolithic `api.ts`. Behavior is unchanged.
 *
 * Note: `aiTrainingDataApi` continues to live in `api.ts`; consumers that
 * still need it should import from `../api` (re-exported by `api/index.ts`).
 */

import { apiClient as fetchClient } from './client';
import { _unwrap } from './_unwrap';
import { API_CONFIG } from './config';
import {
  CloudProvider,
  SeverityLevel,
  AIAsset,
  AIAssetDetail,
  AIRisk,
  TrainingDataset,
  AIAssetType,
} from '../../types';

// ============================================================================
// AI Security API (AI-SPM Module)
// ============================================================================


export const aiSecurityApi = {
  // AI Model Inventory
  getAIAssets: async (params?: {
    asset_type?: AIAssetType;
    provider?: CloudProvider;
    service?: string;
    risk_level?: SeverityLevel;
    is_public?: boolean;
    is_llm?: boolean;
    page?: number;
    page_size?: number;
  }): Promise<{ ai_assets: AIAsset[]; total: number }> => {
    return _unwrap(await fetchClient.get<{ ai_assets: AIAsset[]; total: number }>('/api/v1/ai-spm/models', params));
  },

  getAIAsset: async (id: string): Promise<AIAssetDetail> => {
    return _unwrap(await fetchClient.get<AIAssetDetail>(`/api/v1/ai-spm/models/${id}`));
  },

  getAIModels: async (limit?: number): Promise<{ models: AIAsset[]; total: number }> => {
    return _unwrap(await fetchClient.get<{ models: AIAsset[]; total: number }>('/api/v1/ai-spm/models', { limit }));
  },

  getLLMAssets: async (limit?: number): Promise<{ llm_assets: AIAsset[]; total: number }> => {
    return _unwrap(await fetchClient.get<{ llm_assets: AIAsset[]; total: number }>('/api/v1/ai-spm/models/llm', { limit }));
  },

  getPublicAIAssets: async (limit?: number): Promise<{ public_assets: AIAsset[]; total: number }> => {
    return _unwrap(await fetchClient.get<{ public_assets: AIAsset[]; total: number }>('/api/v1/ai-spm/models/public', { limit }));
  },

  getHighRiskAIAssets: async (
    minScore?: number,
    limit?: number
  ): Promise<{ high_risk_assets: AIAsset[]; total: number }> => {
    return _unwrap(await fetchClient.get<{ high_risk_assets: AIAsset[]; total: number }>('/api/v1/ai-spm/models/high-risk', { min_score: minScore, limit }));
  },

  // AI Model Security
  getAIRisks: async (params?: {
    severity?: SeverityLevel;
    risk_type?: string;
    category?: string;
    status?: string;
    limit?: number;
  }): Promise<{ risks: AIRisk[]; total: number }> => {
    return _unwrap(await fetchClient.get<{ risks: AIRisk[]; total: number }>('/api/v1/ai-spm/security/assessments', params));
  },

  getAIRisk: async (id: string): Promise<AIRisk> => {
    return _unwrap(await fetchClient.get<AIRisk>(`/api/v1/ai-spm/security/assessments/${id}`));
  },

  runSecurityAssessment: async (modelId: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/ai-spm/security/models/${modelId}/assess`));
  },

  getAssetRisks: async (
    assetId: string,
    params?: { status?: string; limit?: number }
  ): Promise<{ asset_id: string; risks: AIRisk[]; total: number }> => {
    return _unwrap(await fetchClient.get<{ asset_id: string; risks: AIRisk[]; total: number }>(`/api/v1/ai-spm/security/models/${assetId}/risks`, params));
  },

  getCriticalAIRisks: async (limit?: number): Promise<{ critical_risks: AIRisk[]; total: number }> => {
    return _unwrap(await fetchClient.get<{ critical_risks: AIRisk[]; total: number }>('/api/v1/ai-spm/security/assessments/critical', { limit }));
  },

  updateAIRiskStatus: async (
    id: string,
    status: string,
    mitigatedBy?: string
  ): Promise<{ status: string; risk: AIRisk }> => {
    return _unwrap(await fetchClient.patch<{ status: string; risk: AIRisk }>(`/api/v1/ai-spm/security/assessments/${id}/status`, {
      status,
      mitigated_by: mitigatedBy,
    }));
  },

  // AI Data Governance
  getTrainingDatasets: async (params?: {
    data_classification?: string;
    contains_pii?: boolean;
    provider?: CloudProvider;
    limit?: number;
  }): Promise<{ training_datasets: TrainingDataset[]; total: number }> => {
    return _unwrap(await fetchClient.get<{ training_datasets: TrainingDataset[]; total: number }>('/api/v1/ai-spm/data-governance/datasets', params));
  },

  getTrainingDataset: async (id: string): Promise<TrainingDataset> => {
    return _unwrap(await fetchClient.get<TrainingDataset>(`/api/v1/ai-spm/data-governance/datasets/${id}`));
  },

  getSensitiveDatasets: async (
    limit?: number
  ): Promise<{ sensitive_datasets: TrainingDataset[]; total: number }> => {
    return _unwrap(await fetchClient.get<{ sensitive_datasets: TrainingDataset[]; total: number }>('/api/v1/ai-spm/data-governance/datasets/sensitive', { limit }));
  },

  // AI Pipeline Security
  getPipelines: async (): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>('/api/v1/ai-spm/pipelines'));
  },

  getPipeline: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ai-spm/pipelines/${id}`));
  },

  getPipelineFindings: async (pipelineId: string): Promise<any[]> => {
    return _unwrap(await fetchClient.get<any[]>(`/api/v1/ai-spm/pipelines/${pipelineId}/findings`));
  },

  scanPipeline: async (pipelineId: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/ai-spm/pipelines/${pipelineId}/scan`));
  },

  getDashboard: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/dashboard'));
  },

  // Model Inventory - Expanded
  getModelVersions: async (modelId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ai-spm/inventory/models/${modelId}/versions`));
  },
  getModelProvenance: async (modelId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ai-spm/inventory/models/${modelId}/provenance`));
  },
  getModelDependencies: async (modelId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ai-spm/inventory/models/${modelId}/dependencies`));
  },
  getModelUsage: async (modelId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ai-spm/inventory/models/${modelId}/usage`));
  },
  getModelDrift: async (modelId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ai-spm/inventory/models/${modelId}/drift`));
  },
  getDeprecatedFrameworks: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/inventory/deprecated-frameworks'));
  },
  getModelSupplyChain: async (modelId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ai-spm/inventory/models/${modelId}/supply-chain`));
  },

  // Model Security - Expanded
  runPromptInjectionTest: async (modelId: string, data?: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/ai-spm/security/models/${modelId}/prompt-injection`, data));
  },
  runAdversarialTest: async (modelId: string, data?: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/ai-spm/security/models/${modelId}/adversarial-test`, data));
  },
  getModelExtractionRisks: async (modelId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ai-spm/security/models/${modelId}/extraction-risks`));
  },
  getOutputSafety: async (modelId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ai-spm/security/models/${modelId}/output-safety`));
  },
  getGuardrails: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/security/guardrails'));
  },
  getModelAttackSurface: async (modelId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ai-spm/security/models/${modelId}/attack-surface`));
  },

  // Data Governance - Expanded
  getDatasetAccessLogs: async (datasetId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ai-spm/data-governance/datasets/${datasetId}/access-logs`));
  },
  classifyDataset: async (datasetId: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/ai-spm/data-governance/datasets/${datasetId}/classify`));
  },
  scanDatasetPII: async (datasetId: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/ai-spm/data-governance/datasets/${datasetId}/pii-scan`));
  },
  getConsentTracking: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/data-governance/consent-tracking'));
  },
  getRetentionPolicies: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/data-governance/retention-policies'));
  },
  getDataQuality: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/data-governance/data-quality'));
  },
  getCrossBorderCompliance: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/data-governance/cross-border'));
  },
  getDPIA: async (datasetId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ai-spm/data-governance/datasets/${datasetId}/dpia`));
  },

  // Pipeline Security - Expanded
  getPipelineArtifacts: async (pipelineId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ai-spm/pipelines/${pipelineId}/artifacts`));
  },
  getPipelineSigning: async (pipelineId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ai-spm/pipelines/${pipelineId}/signing`));
  },
  verifyPipeline: async (pipelineId: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/ai-spm/pipelines/${pipelineId}/verify`));
  },
  getPipelineDependencies: async (pipelineId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ai-spm/pipelines/${pipelineId}/dependencies`));
  },
  getPipelineAccessControl: async (pipelineId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ai-spm/pipelines/${pipelineId}/access-control`));
  },
  getPipelineSupplyChainRisks: async (pipelineId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ai-spm/pipelines/${pipelineId}/supply-chain-risks`));
  },
  getPipelineCICD: async (pipelineId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ai-spm/pipelines/${pipelineId}/cicd`));
  },
  getPipelineCompliance: async (pipelineId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ai-spm/pipelines/${pipelineId}/compliance`));
  },
  scanPipelineSecrets: async (pipelineId: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/ai-spm/pipelines/${pipelineId}/secrets-scan`));
  },

  // Aggregator - Expanded
  getRiskHistory: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/risk-history'));
  },
  getAIRemediation: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/remediation'));
  },
  getAISupplyChain: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/supply-chain'));
  },
  getGovernancePolicies: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/governance-policies'));
  },
  getEthicsScore: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/ethics-score'));
  },
  getAIIncidents: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/incidents'));
  },

  // Agent Security
  getAgents: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/agents'));
  },
  getAgent: async (agentId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ai-spm/agents/${agentId}`));
  },
  getMCPConnections: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/agents/mcp-connections'));
  },
  getAgentPolicies: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/agents/policies'));
  },
  getAgentAnomalies: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/agents/anomalies'));
  },
  auditMCPConnection: async (connectionId: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/ai-spm/agents/mcp-connections/${connectionId}/audit`));
  },
  getAgentGraph: async (agentId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ai-spm/agents/${agentId}/graph`));
  },

  // Runtime Monitor
  getRuntimeDeployments: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/runtime/deployments'));
  },
  getRuntimeDeployment: async (deploymentId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ai-spm/runtime/deployments/${deploymentId}`));
  },
  getDriftAlerts: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/runtime/drift-alerts'));
  },
  getRuntimeThreats: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/runtime/threats'));
  },
  getInferenceEvents: async (params?: { limit?: number; flagged?: boolean }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/runtime/inference-events', params));
  },
  getRuntimeAnomalies: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/runtime/anomalies'));
  },
  getRuntimeAlerts: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/runtime/alerts'));
  },
  getInferenceStats: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/statistics'));
  },

  // Security Testing
  getPromptInjectionTests: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/security/prompt-injection-tests'));
  },
  getAdversarialTests: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/security/adversarial-tests'));
  },
  getOutputSafetyChecks: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/security/output-safety'));
  },
  getGuardrailConfigs: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/security/guardrails'));
  },
  getArtifactScans: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/security/artifact-scans'));
  },

  // AI Attack Path
  getAttackPaths: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/attack-paths'));
  },
  getAttackPath: async (pathId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ai-spm/attack-paths/${pathId}`));
  },
  getToxicCombinations: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/attack-paths/toxic-combinations'));
  },
  getSecurityGraph: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/attack-paths/graph'));
  },
  getBlastRadius: async (nodeId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ai-spm/attack-paths/blast-radius/${nodeId}`));
  },
  simulateRisk: async (scenario: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/ai-spm/attack-paths/simulate', scenario));
  },

  // AI-BOM
  getAIBOM: async (modelId: string, format?: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ai-spm/inventory/models/${modelId}/ai-bom`, { format }));
  },

  // AI Compliance
  getAIComplianceFrameworks: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/data-governance/compliance/frameworks'));
  },
  getAIComplianceAssessment: async (frameworkId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ai-spm/data-governance/compliance/frameworks/${frameworkId}/assess`));
  },

  // Pipeline Provenance
  getPipelineProvenance: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ai-spm/pipelines/${id}/provenance`));
  },

  verifyProvenance: async (id: string, data: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/ai-spm/pipelines/${id}/provenance/verify`, data));
  },

  // Runtime Events SSE Stream
  streamRuntimeEvents: (onMessage: (event: any) => void, onError?: (error: any) => void): EventSource => {
    const url = `${API_CONFIG.BASE_URL}/api/v1/ai-spm/runtime/events/stream`;
    const eventSource = new EventSource(url);
    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        onMessage(data);
      } catch {
        onMessage(event.data);
      }
    };
    if (onError) {
      eventSource.onerror = onError;
    }
    return eventSource;
  },

  // ---- NEW: AI Vulnerability Database (Model Security 8071) ----
  getAIVulnerabilityDB: async (params?: { severity?: string; affected_package?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/security/vulnerability-db', params));
  },
  getAIVulnerability: async (vulnId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ai-spm/security/vulnerability-db/${vulnId}`));
  },
  getSupplyChainRisks: async (params?: { risk_level?: string; package_name?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/security/supply-chain/risks', params));
  },
  scanSupplyChain: async (data?: { package_name?: string; version?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/ai-spm/security/supply-chain/scan', data));
  },

  // ---- NEW: Data Poisoning Detection (Data Governance 8072) ----
  getDataPoisoningDetections: async (params?: { severity?: string; detection_type?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/data-governance/data-poisoning/detections', params));
  },
  getDataPoisoningDetection: async (detectionId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ai-spm/data-governance/data-poisoning/detections/${detectionId}`));
  },
  scanDataPoisoning: async (data: { dataset_id: string; scan_type?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/ai-spm/data-governance/data-poisoning/scan', data));
  },
  getDataIntegrity: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/data-governance/data-integrity'));
  },

  // ---- NEW: Model Registry Security (Pipeline Security 8073) ----
  getModelRegistry: async (params?: { registry_type?: string; status?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/pipelines/model-registry', params));
  },
  getModelRegistryEntry: async (entryId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ai-spm/pipelines/model-registry/${entryId}`));
  },
  approveModelRegistryEntry: async (entryId: string, data?: { approved_by?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/ai-spm/pipelines/model-registry/${entryId}/approve`, data));
  },
  getModelRegistrySecurityFindings: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/pipelines/model-registry/security-findings'));
  },
  getModelRegistryStatistics: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/statistics'));
  },

  // ---- NEW: Rogue Agent Detection & MCP Compliance (Agent Security 8074) ----
  getRogueAgents: async (params?: { status?: string; detection_type?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/agents/rogue-agents', params));
  },
  getRogueAgent: async (detectionId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ai-spm/agents/rogue-agents/${detectionId}`));
  },
  scanRogueAgents: async (): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/ai-spm/agents/rogue-agents/scan'));
  },
  getMCPCompliance: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/agents/mcp/compliance'));
  },

  // ---- NEW: Alert Rules Engine & Performance Baselines (Runtime Monitor 8075) ----
  getAlertRules: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/runtime/alert-rules'));
  },
  createAlertRule: async (rule: any): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/ai-spm/runtime/alert-rules', rule));
  },
  toggleAlertRule: async (ruleId: string): Promise<any> => {
    return _unwrap(await fetchClient.put<any>(`/api/v1/ai-spm/runtime/alert-rules/${ruleId}/toggle`));
  },
  deleteAlertRule: async (ruleId: string): Promise<any> => {
    return _unwrap(await fetchClient.delete<any>(`/api/v1/ai-spm/runtime/alert-rules/${ruleId}`));
  },
  getPerformanceBaselines: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/runtime/performance-baselines'));
  },
  getPerformanceBaseline: async (deploymentId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ai-spm/runtime/performance-baselines/${deploymentId}`));
  },
  recalculateBaseline: async (deploymentId: string): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/ai-spm/runtime/performance-baselines/${deploymentId}/recalculate`));
  },

  // ---- NEW: Compliance Mapping (AI Attack Path 8076) ----
  getComplianceMapping: async (params?: { framework_name?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/attack-paths/compliance-mapping', params));
  },
  getComplianceMappingById: async (mappingId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ai-spm/attack-paths/compliance-mapping/${mappingId}`));
  },
  getComplianceMappingByFramework: async (frameworkName: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ai-spm/attack-paths/compliance-mapping/by-framework/${frameworkName}`));
  },
  getCompliancePosture: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/attack-paths/compliance-posture'));
  },

  // ---- NEW: Shadow AI Findings (Model Inventory 8070) ----
  getShadowAIFindings: async (params?: { severity?: string; status?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/inventory/shadow-ai', params));
  },
  getShadowAIFinding: async (findingId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ai-spm/inventory/shadow-ai/${findingId}`));
  },
  scanShadowAI: async (): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/ai-spm/inventory/shadow-ai/scan'));
  },
  // Umbrella AI-SPM scan: fans out red-team (prompt-injection + output-safety)
  // over every discovered model, shadow-AI, supply-chain & data-poisoning.
  // Returns per-module status; honest `not_configured` when no LLM endpoint set.
  runSecurityScan: async (data?: Record<string, unknown>): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/ai-spm/scan', data || {}));
  },
  getShadowAIStatistics: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/shadow-ai'));
  },
  acknowledgeShadowAI: async (findingId: string, data?: { action?: string; notes?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>(`/api/v1/ai-spm/inventory/shadow-ai/${findingId}/acknowledge`, data));
  },

  // ---- NEW: AI-BOM export helper ----
  exportAIBom: async (format?: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/inventory/models/bom/export', { format }));
  },

  // ---- NEW: AI-BOM aggregated (no model id needed) ----
  getAIBom: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/inventory/ai-bom'));
  },

  // ---- LLM Security Scanner ----
  scanPromptForInjection: async (data: { prompt: string; context?: string; model_id?: string; scan_depth?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/ai-spm/llm/scan-prompt', data));
  },
  scanPromptsBatch: async (data: { prompts: string[]; model_id?: string; scan_depth?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/ai-spm/llm/scan-batch', data));
  },
  getInjectionPatterns: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/llm/injection-patterns'));
  },
  addInjectionPattern: async (data: { name: string; pattern: string; description?: string; severity?: string; category?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/ai-spm/llm/injection-patterns', data));
  },
  getLLMScanResults: async (params?: { limit?: number; offset?: number; risk_level?: string; is_injection?: boolean }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/llm/scan-results', params));
  },
  getLLMScanStats: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/llm/stats'));
  },

  // ---- Model Security ----
  scanModelSecurity: async (data: { model_id: string; model_name?: string; model_type?: string; framework?: string; framework_version?: string; cloud_provider?: string; endpoint_url?: string; scan_types?: string[] }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/ai-spm/models/scan', data));
  },
  getModelVulnerabilities: async (modelId: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/ai-spm/models/${modelId}/vulnerabilities`));
  },
  auditModelAccess: async (data: { model_id: string; cloud_provider?: string; service?: string; iam_policies?: any[]; endpoint_config?: any }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/ai-spm/models/access-audit', data));
  },
  getPublicModelEndpoints: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/models/public-endpoints'));
  },
  checkModelProvenance: async (data: { model_id: string; model_name?: string; source_registry?: string; model_hash?: string; training_dataset_hashes?: string[]; parent_model?: string; fine_tune_source?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/ai-spm/models/provenance', data));
  },

  // ---- Training Data Security ----
  scanTrainingData: async (data: { dataset_id: string; dataset_name?: string; data_samples?: string[]; data_hashes?: string[]; expected_hashes?: string[]; check_pii?: boolean; check_poisoning?: boolean }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/ai-spm/training/scan', data));
  },
  getTrainingIntegrity: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/training/integrity'));
  },
  detectTrainingPoisoning: async (data: { dataset_id: string; data_samples?: string[]; labels?: string[]; expected_label_distribution?: Record<string, number> }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/ai-spm/training/detect-poisoning', data));
  },
  getTrainingPIIExposure: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/training/pii-exposure'));
  },

  // ---- AI Supply Chain Security ----
  getMLDependencies: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/supply-chain/dependencies'));
  },
  getSupplyChainVulnerabilities: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/supply-chain/vulnerabilities'));
  },
  scanMLSupplyChain: async (data: { dependencies: Array<{ name: string; version: string; signature_verified?: boolean }>; scan_transitive?: boolean; check_signatures?: boolean }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/ai-spm/supply-chain/scan', data));
  },
  getMLSBOM: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/supply-chain/sbom'));
  },

  // ---- AI Security Dashboard ----
  getAISecurityDashboard: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/security/dashboard'));
  },
  getAIRiskScore: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/security/risk-score'));
  },
  getAISecurityTrends: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/security/trends'));
  },
};

// ── AI Firewall API (R187-C — Stage-27 P1) ────────────────────────────────────
// 7 BE routes live in services/domain/ai-spm/app/children/runtime_monitor/main.py
// — pre-R187-C there was zero FE consumer. R40/R41 pattern (wired-but-rendered-null)
// repeated earlier in the lifecycle: BE routes shipped without an FE entry point.
export const firewallApi = {
  getRules: async (params?: { enabled_only?: boolean; threat_category?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/runtime/firewall/policies', params));
  },
  createRule: async (rule: {
    name: string;
    description?: string;
    threat_category: string;
    action?: string;
    severity?: string;
    enabled?: boolean;
    config?: Record<string, any>;
  }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/ai-spm/runtime/firewall/policies', rule));
  },
  updateRule: async (id: string, rule: any): Promise<any> => {
    return _unwrap(await fetchClient.put<any>(`/api/v1/ai-spm/runtime/firewall/policies/${id}`, rule));
  },
  deleteRule: async (id: string): Promise<any> => {
    // Aligns with REST convention: PUT to disable. Hard delete not exposed BE-side;
    // soft-disable via {enabled: false} is the documented mutation.
    return _unwrap(await fetchClient.put<any>(`/api/v1/ai-spm/runtime/firewall/policies/${id}`, { enabled: false }));
  },
  testPrompt: async (data: { content: string; direction?: string; policy_ids?: string[] }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/ai-spm/runtime/firewall/test', data));
  },
  evaluate: async (data: {
    model_id: string;
    direction?: string;
    content: string;
    user_id?: string;
    session_id?: string;
    metadata?: Record<string, any>;
  }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/ai-spm/runtime/firewall/evaluate', data));
  },
  getStats: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/runtime/firewall/statistics'));
  },
  getViolations: async (params?: {
    limit?: number;
    offset?: number;
    action?: string;
    threat_category?: string;
    model_id?: string;
  }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/runtime/firewall/logs', params));
  },
};

// ── Token Abuse API (R187-C — Stage-27 P1) ────────────────────────────────────
// 4 policy types BE-side (per_request_limit / daily_budget / rate_limit / anomaly_detection).
// Lives in runtime_monitor/main.py Gap-24 LLM Cost/Token-Abuse section.
export const tokenAbuseApi = {
  getPolicies: async (params?: { policy_type?: string; enabled?: boolean }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/runtime/token-abuse/policies', params));
  },
  createPolicy: async (policy: {
    name: string;
    description?: string;
    policy_type: string;
    enabled?: boolean;
    config: Record<string, any>;
  }): Promise<any> => {
    return _unwrap(await fetchClient.post<any>('/api/v1/ai-spm/runtime/token-abuse/policies', policy));
  },
  getViolations: async (params?: {
    detection_type?: string;
    severity?: string;
    status?: string;
    model_id?: string;
    limit?: number;
    offset?: number;
  }): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/runtime/token-abuse/detections', params));
  },
  getQuotas: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/ai-spm/runtime/token-abuse/statistics'));
  },
};

// ── AI Assistant API ──────────────────────────────────────────────────────────
export const aiAssistantApi = {
  chat: async (message: string, history: any[] = [], tenantId = 'demo-tenant-001'): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/assistant/chat', { message, history, tenant_id: tenantId }));
  },
  graphQuery: async (query: string, tenantId = 'demo-tenant-001'): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/assistant/graph-query', { query, tenant_id: tenantId }));
  },
  getSuggestions: async (context?: string): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/assistant/suggestions', { context }));
  },
};

// ── AI-SPM API ────────────────────────────────────────────────────────────────
export const aiSpmApi = {
  getDashboard: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/ai-spm/dashboard'));
  },
  getModels: async (params?: { risk_level?: string; provider?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/ai-spm/models', params));
  },
  getFindings: async (params?: { severity?: string; category?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/ai-spm/findings', params));
  },
  getRuntimeThreats: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/ai-spm/runtime/threats'));
  },
  getPipelineSecurity: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/ai-spm/pipelines'));
  },
};
